import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Card } from "@/shared/ui/card";
import { useLanguage } from "@/shared/hooks/use-language";

/**
 * One slice of a portfolio breakdown. `share: null` is a bucket the reader must be told
 * about but whose share cannot be measured: it gets a legend entry and no wedge, never a
 * zero-size wedge or a made-up 0%.
 */
export interface BreakdownBucket {
  key: string;
  label: string;
  weightLabel: string;
  share: { amount: bigint; scale: number } | null;
  color: string;
  testId?: string;
}

interface BreakdownDonutProps {
  title: string;
  testIdPrefix: string;
  buckets: BreakdownBucket[];
  notice: string | null;
  wholeIsNegative: boolean;
}

export const OTHER_COLOR = "hsl(var(--muted-foreground))";
const SURFACE = "hsl(var(--card))";

/**
 * The donut both allocation cards draw, so their degraded states cannot drift apart.
 * Every state other than "some share was read" is the notice card: the caller words it.
 *
 * A pie can only draw a non-negative part of a positive whole. A book net of short
 * positions has a negative share (and so another above 100%), or a negative whole, and
 * a pie draws either one as a positive slice. Such a breakdown keeps every weight in the
 * legend and draws no ring, with a note that says why.
 */
export const BreakdownDonut = ({ title, testIdPrefix, buckets, notice, wholeIsNegative }: BreakdownDonutProps) => {
  const { t } = useLanguage();
  const measuredBuckets = buckets.filter((bucket) => bucket.share !== null);

  if (measuredBuckets.length === 0) {
    if (notice === null) return null;
    return (
      <Card className="p-6 text-sm text-muted-foreground" data-testid={`${testIdPrefix}-unavailable`}>
        <Title title={title} testIdPrefix={testIdPrefix} />
        <p>{notice}</p>
        {buckets.length > 0 && <Legend testIdPrefix={testIdPrefix} buckets={buckets} isRingDrawn={false} className="mt-4" />}
      </Card>
    );
  }

  const isNetOfShorts = wholeIsNegative || measuredBuckets.some((bucket) => bucket.share!.amount < 0n);
  if (isNetOfShorts) {
    return (
      <Card className="p-6">
        <Title title={title} testIdPrefix={testIdPrefix} />
        <p className="mb-4 text-sm text-muted-foreground" data-testid={`${testIdPrefix}-net-of-shorts`}>
          {t("portfolio.breakdownNetOfShorts")}
        </p>
        <Legend testIdPrefix={testIdPrefix} buckets={buckets} isRingDrawn={false} />
      </Card>
    );
  }

  const data = measuredBuckets.map((bucket) => ({
    name: bucket.key,
    label: bucket.label,
    weightLabel: bucket.weightLabel,
    // The only place a share becomes a float, because a wedge angle is geometry rather
    // than a figure anyone reads. What the reader reads is `weightLabel`.
    value: Number(bucket.share!.amount) / 10 ** bucket.share!.scale,
    color: bucket.color,
  }));

  return (
    <Card className="p-6">
      <Title title={title} testIdPrefix={testIdPrefix} />
      <div className="flex flex-wrap items-center gap-8">
        <div className="w-48 h-48" data-testid={`${testIdPrefix}-ring`}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="label"
                innerRadius="60%"
                outerRadius="100%"
                stroke={SURFACE}
                strokeWidth={2}
                isAnimationActive={false}
              >
                {data.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(_value: number, _name: string, item: { payload?: { weightLabel?: string } }) =>
                  item?.payload?.weightLabel ?? ""
                }
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <Legend testIdPrefix={testIdPrefix} buckets={buckets} isRingDrawn />
      </div>
    </Card>
  );
};

const Title = ({ title, testIdPrefix }: { title: string; testIdPrefix: string }) => (
  <h3 className="mb-4 text-base font-semibold text-foreground" data-testid={`${testIdPrefix}-title`}>{title}</h3>
);

/**
 * Only a measured entry carries the `-legend-label` / `-legend-weight` test ids, so those
 * read exactly the weights that add up to the whole. An unmeasured entry is found by its
 * own `testId`. A swatch is filled only when a wedge of its color is drawn; otherwise it
 * is an empty outline, because it has no wedge to match.
 */
const Legend = ({ testIdPrefix, buckets, isRingDrawn, className = "" }: {
  testIdPrefix: string;
  buckets: BreakdownBucket[];
  isRingDrawn: boolean;
  className?: string;
}) => (
  <ul className={`space-y-2 ${className}`} data-testid={`${testIdPrefix}-legend`}>
    {buckets.map((bucket) => {
      const isMeasured = bucket.share !== null;
      const hasWedge = isMeasured && isRingDrawn;
      return (
        <li key={bucket.key} className="flex items-center gap-2 text-sm" data-testid={bucket.testId}>
          <span
            aria-hidden="true"
            className={`inline-block w-3 h-3 rounded-sm ${hasWedge ? "" : "border"}`}
            style={hasWedge ? { backgroundColor: bucket.color } : { borderColor: bucket.color }}
          />
          <span
            className="text-foreground"
            data-testid={isMeasured ? `${testIdPrefix}-legend-label` : undefined}
          >
            {bucket.label}
          </span>
          <span
            className="text-muted-foreground tabular-nums"
            data-testid={isMeasured ? `${testIdPrefix}-legend-weight` : undefined}
          >
            {bucket.weightLabel}
          </span>
        </li>
      );
    })}
  </ul>
);
